'use client';

import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { VS_URL } from '@/shared/model/constants/url';
import PcAdaptiveLink from '@/shared/ui/adaptiveLink/PcAdaptiveLink';
import PcButton from '@/shared/ui/button/PcButton';
import ImageWithSkeleton from '@/shared/ui/image/ImageWithSkeleton';
import PcProgressBar from '@/shared/ui/progress/ui/PcProgressBar';
import { ArrowIcon } from '@/shared/ui/svgs';
import DownloadIcon from '@/shared/ui/svgs/download_icon.svg';
import PcTypography from '@/shared/ui/typogrphy/PcTypography';

const images = [
  '/images/visa-guide/visa-guide-image1.jpg',
  '/images/visa-guide/visa-guide-image2.jpg',
  '/images/visa-guide/visa-guide-image3.jpg',
  '/images/visa-guide/visa-guide-image4.jpg',
  '/images/visa-guide/visa-guide-image5.jpg',
];

export default function PcVisaGuide() {
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType>(null);
  const t = useTranslations('visa-guide');
  const totalSlides = images.length;

  return (
    <div className="flex flex-col items-center pt-30 max-w-[1280px] mx-auto">
      <PcTypography id="visa-guide-h1" variant="heading">
        {t('title')}
      </PcTypography>
      <div className="w-full px-3">
        <div className="flex items-center justify-between pt-20">
          <div>
            <PcAdaptiveLink type="download" href={VS_URL.VISA_APPLICATION_FORM}>
              <PcButton
                width={225}
                height={60}
                shape="rectangle"
                variant="solidPrimary"
                className="rounded-[40px] !text-[18px] gap-1"
              >
                {t('btn.download')}
                <DownloadIcon />
              </PcButton>
            </PcAdaptiveLink>
          </div>
          <div className="flex items-center justify-between">
            <div className="pr-2">
              <PcAdaptiveLink type="external" href={VS_URL.VISA_NAVIGATOR}>
                <PcButton
                  width={149}
                  height={60}
                  shape="rectangle"
                  variant="outlineWhite"
                  className="!text-[16px] !font-medium"
                >
                  {t('btn.navigator')}
                </PcButton>
              </PcAdaptiveLink>
            </div>
            <PcAdaptiveLink type="external" href={VS_URL.VISA_REQUIRED_DOCUMENTS_INFO}>
              <PcButton
                width={212}
                height={60}
                shape="rectangle"
                variant="outlineWhite"
                className="!text-[16px] !font-medium"
              >
                {t('btn.required_documents_info')}
              </PcButton>
            </PcAdaptiveLink>
          </div>
        </div>
        <div className="w-[1280px] h-[1160px] bg-[#F6F6F8] mt-10 mb-30 rounded-4xl">
          <div className="flex p-30">
            <div className="relative w-[620px] h-[920px]">
              {/* Swiper PcSectionContainer */}
              <Swiper
                modules={[Navigation, Pagination]}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                }}
                onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                className="rounded-lg overflow-hidden"
              >
                {images.map((src, index) => (
                  <SwiperSlide key={index}>
                    <div className="relative w-[620px] h-[920px]">
                      <ImageWithSkeleton
                        src={src}
                        fill
                        alt={`visa-guide-${index + 1}`}
                        className="object-cover"
                        quality={90}
                        priority={index === 0}
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
            <div className="h-full pl-20 pt-30">
              <PcTypography variant="heading2">{t('sub_title')}</PcTypography>
              <div className="flex gap-[12px] pt-6">
                <PcButton
                  shape="circle"
                  variant="outline"
                  className={`w-[48px] h-[48px] ${activeIndex < 1 ? 'border-[#E5E5E5]' : 'bg-black'}`}
                  onClick={() => swiperRef.current?.slidePrev()}
                >
                  <ArrowIcon color={activeIndex < 1 ? 'black' : 'white'} direction={'left'} />
                </PcButton>
                <PcButton
                  shape="circle"
                  variant={activeIndex >= totalSlides - 1 ? 'outline' : undefined}
                  className={`w-[48px] h-[48px] ${activeIndex >= totalSlides - 1 ? 'border-[#E5E5E5]' : 'bg-black'}`}
                  onClick={() => swiperRef.current?.slideNext()}
                >
                  <ArrowIcon
                    color={activeIndex >= totalSlides - 1 ? 'black' : 'white'}
                    direction={'right'}
                  />
                </PcButton>
              </div>

              {/* Progress Bar */}
              <div className="pt-14 flex items-center gap-4">
                <PcProgressBar activeIndex={activeIndex} totalSlides={totalSlides} />
                <div className="text-sm font-medium">
                  <span className="text-[#101010]">{String(activeIndex + 1).padStart(2, '0')}</span>
                  <span className="text-[#848484]"> / {String(totalSlides).padStart(2, '0')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
